import React, { useState, useEffect, useContext } from "react";
import { Trash, Plus } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { graphThemes } from "@/data/colors";
import GraphConfigFormContext from "@/store/contexts/GraphConfigFormContext";

type ThemeType =
  | "default"
  | "earthy"
  | "pastel"
  | "neon"
  | "midnight"
  | "custom";

interface ThemeSelectorProps {}

const ThemeSelector = ({}: ThemeSelectorProps) => {
  const { config, setConfig } = useContext(GraphConfigFormContext);
  
  if (!config) return null;
  
  const theme = config.graph_options.theme;
  const colors = config.graph_options.colors;
  const [customColors, setCustomColors] = useState<string[]>(
    theme === "custom" ? colors : [...graphThemes.default]
  );

  // Initialize component state from config
  useEffect(() => {
    setCustomColors(
      theme === "custom" ? colors : [...graphThemes[theme] || graphThemes.default]
    );
  }, [theme, colors]);

  // Handle color change
  const handleColorChange = (index: number, value: string) => {
    const newColors = [...customColors];
    newColors[index] = value;
    setCustomColors(newColors);

    if (theme === "custom") {
      setConfig({
        ...config,
        graph_options: {
          ...config.graph_options,
          theme,
          colors: newColors,
        },
      });
    }
  };

  // Add a new color
  const handleAddColor = () => {
    const newColors = [...customColors, "#000000"];
    setCustomColors(newColors);

    if (theme === "custom") {
      setConfig({
        ...config,
        graph_options: {
          ...config.graph_options,
          theme,
          colors: newColors,
        },
      });
    }
  };

  // Remove a color
  const handleRemoveColor = (index: number) => {
    const newColors = [...customColors];
    newColors.splice(index, 1);
    setCustomColors(newColors);

    if (theme === "custom") {
      setConfig({
        ...config,
        graph_options: {
          ...config.graph_options,
          theme,
          colors: newColors,
        },
      });
    }
  };

  // Handle theme change
  const handleThemeChange = (value: ThemeType) => {
    if (value !== "custom") {
      const themeColors = [...graphThemes[value]];
      setCustomColors(themeColors);
      setConfig({
        ...config,
        graph_options: {
          ...config.graph_options,
          theme: value,
          colors: themeColors,
        },
      });
    } else {
      setConfig({
        ...config,
        graph_options: {
          ...config.graph_options,
          theme: value,
          colors: customColors,
        },
      });
    }
  };

  // Render the color preview
  const renderColorPreview = (colors: string[]) => {
    return (
      <div className="flex gap-1 items-center">
        {colors.map((color, index) => (
          <div
            key={index}
            style={{ backgroundColor: color }}
            className="w-4 h-4 rounded-xs shadow"
          />
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Theme</Label>
        <div className="w-[70%]">
          <Select
            value={theme}
            onValueChange={(value: ThemeType) => handleThemeChange(value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select theme" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {Object.entries(graphThemes).map(([themeName, colors]) => (
                  <SelectItem
                    key={themeName}
                    value={themeName as ThemeType}
                    className="flex items-center justify-between w-full"
                  >
                    <div className="flex items-center justify-between min-w-[240px]">
                      <span className="capitalize">{themeName}</span>
                      {renderColorPreview(colors)}
                    </div>
                  </SelectItem>
                ))}
                <SelectItem value="custom">Custom</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      </div>

      {theme === "custom" && (
        <div className="mt-2">
          <Label className="text-sm font-medium mb-2 block">
            Custom Colors
          </Label>
          <div className="flex flex-col gap-2">
            {customColors.map((color, index) => (
              <div className="flex flex-row w-full items-center">
                <div className="flex items-center gap-2 flex-1">
                  <Input
                    type="color"
                    value={color}
                    onChange={(e) => handleColorChange(index, e.target.value)}
                    className="w-10 h-10 p-1"
                  />
                  <Input
                    type="text"
                    value={color}
                    onChange={(e) => handleColorChange(index, e.target.value)}
                    className="flex-1"
                  />
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 ml-2"
                  onClick={() => handleRemoveColor(index)}
                  disabled={customColors.length <= 1}
                >
                  <Trash className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              className="w-full mt-2"
              onClick={handleAddColor}
            >
              <Plus className="h-4 w-4 mr-2" /> Add Color
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThemeSelector;
